import { NOT_STARTED, IN_PROGRESS, COMPLETED } from 'src/constants';
import {
  diff,
  getEstimate,
  getScheduledStarts,
  getPredictedScheduledStarts,
  getTime
} from 'src/lib/time';

export const isStatus = (status, target) => {
  if (typeof target === 'string') {
    return status === target;
  }
  if (!target.status) {
    throw new Error(
      'Invalid target type for isStatus. Must be a string or obj with property status'
    );
  }
  return target.status === status;
};

export const isNotStarted = (jobOrStage) => isStatus(NOT_STARTED, jobOrStage);
export const isInProgress = (jobOrStage) => isStatus(IN_PROGRESS, jobOrStage);
export const isCompleted = (jobOrStage) => isStatus(COMPLETED, jobOrStage);

export const getStatus = ({ start_time, end_time }) => {
  if (!start_time) {
    return NOT_STARTED;
  } else if (start_time && !end_time) {
    return IN_PROGRESS;
  } else if (start_time && end_time) {
    return COMPLETED;
  } else {
    throw new Error('Cannot calculate the status.');
  }
};

export const forcastStages = (job) => {
  const scheduledStarts = getScheduledStarts(job);
  const predictedStarts = getPredictedScheduledStarts(job);

  // Map the stages into items that can be used to get scale and tasks
  return job.stages.map((_stage, index) => {
    const stage = { ..._stage };

    if (isNotStarted(stage)) {
      stage.adjusted_estimate = stage.original_estimate;
    } else if (isInProgress(stage)) {
      stage.adjusted_estimate =
        stage.adjusted_estimate || stage.original_estimate;
    } else if (isCompleted(stage)) {
      stage.adjusted_estimate = diff(stage.end_time, stage.start_time);
    }

    stage.scheduled_start = scheduledStarts[index];
    stage.scheduled_end = getEstimate(
      stage.scheduled_start,
      stage.original_estimate
    );

    stage.adjusted_scheduled_start = predictedStarts[index];
    if (isCompleted(stage)) {
      stage.adjusted_scheduled_end = getTime(stage.end_time);
    } else {
      stage.adjusted_scheduled_end = getEstimate(
        stage.adjusted_scheduled_start,
        stage.adjusted_estimate
      );
    }

    return stage;
  });
};

const getDelta = (date1, date2) => {
  if (date2 < date1) {
    return (date1 - date2) * -1;
  } else {
    return date2 - date1;
  }
};

const applyDelta = (date, delta) => {
  return new Date(getTime(date) + delta);
};

const setNextStages = (startIndex, stages, delta) => {
  for (let i = startIndex; i < stages.length; i++) {
    const stage = stages[i];
    if (isCompleted(stage)) {
      stage.start_time = applyDelta(stage.start_time, delta);
      stage.end_time = applyDelta(stage.end_time, delta);
    }
    if (isInProgress(stage)) {
      stage.start_time = applyDelta(stage.start_time, delta);
    }
  }
};

const setPrevStages = (startIndex, stages, delta) => {
  for (let i = 0; i <= startIndex; i++) {
    const stage = stages[i];
    stage.start_time = applyDelta(stage.start_time, delta);
    stage.end_time = applyDelta(stage.end_time, delta);
  }
};

const getPrevStages = (stages, index) => {
  return stages.slice(0, index);
};

// const getNextStages = (stages, index) => {
//   return stages.slice(index + 1, stages.length);
// };

const validateChange = (job, index, value, type) => {
  const stage = job.stages[index];
  const prevStages = getPrevStages(job.stages, index);

  if (prevStages.find((stage) => !isCompleted(stage))) {
    throw new Error(
      'Cannot set time of a stage if all previous stages are not complete'
    );
  }

  if (type === 'end_time') {
    if (isNotStarted(stage)) {
      throw new Error('Cannot set end time of a "Not Started" stage');
    }
    if (value < stage.start_time) {
      throw new Error('Cannot set end time earlier than start time of stage');
    }
  }

  if (type === 'start_time') {
    if (isCompleted(stage) && value > stage.end_time) {
      throw new Error('Cannot set start time later than end time of stage');
    }
  }
};

export const setStageTime = (_job, index, _value, type) => {
  const value = new Date(_value);
  const job = { ..._job };
  const stages = job.stages.map((stage) => ({ ...stage }));
  const stage = stages[index];
  const lastIndex = stages.length - 1;
  const nextIndex = index + 1;
  const prevIndex = index - 1;
  const nextStage = stages[nextIndex];
  const delta = !stage[type]
    ? 0
    : getDelta(getTime(stage[type]), getTime(value));

  validateChange(job, index, value, type);

  if (type === 'end_time') {
    stage.end_time = value;
    stage.status = getStatus(stage);
    if (index < lastIndex) {
      nextStage.start_time = value;
      nextStage.status = getStatus(nextStage);
      if (isCompleted(nextStage)) {
        nextStage.end_time = applyDelta(nextStage.end_time, delta);
      }
      if (nextIndex + 1 <= lastIndex) {
        setNextStages(nextIndex + 1, stages, delta);
      }
    }
  } else {
    stage.start_time = value;
    stage.status = getStatus(stage);
    if (index !== 0) {
      setPrevStages(prevIndex, stages, delta);
    }
  }

  job.start_time = stages[0].start_time;
  job.end_time = stages[lastIndex].end_time;
  job.status = getStatus(job);
  job.stages = stages;

  return job;
};
